/* Vector Space Chess Club page (kwvector.space/chess).
 *
 * Server-rendered like the rest of the brand — the poster cards and the archive
 * strip come down as HTML and need no JS. This bundle adds:
 *   - lucide icon rendering + the header's scrolled state (same as vectorspace.js)
 *   - the light month calendar (built from a json_script payload)
 *   - jump-to-date: the hero's "next up" strip and each poster card's "find it
 *     on the calendar" button select that day in the calendar and scroll to it
 *   - the family-membership estimator (+/- steppers → live total)
 *   - pass selection (pricing buttons feed the registration form + scroll to it)
 *
 * The pricing/registration sections only exist while passes are on sale, so
 * every handler below tolerates them being absent.
 */

import { initChessHero } from './chess-hero';
import {
    createIcons,
    ArrowRight,
    CalendarDays,
    CalendarPlus,
    Check,
    Clock,
    ExternalLink,
    GraduationCap,
    Image,
    Info,
    Mail,
    Map,
    MapPin,
    Navigation,
    Phone,
    Target,
    Trophy,
    User,
    Users,
} from 'lucide';

const ICONS = {
    ArrowRight, CalendarDays, CalendarPlus, Check, Clock, ExternalLink,
    GraduationCap, Image, Info, Mail, Map, MapPin, Navigation, Phone, Target,
    Trophy, User, Users,
};

const renderIcons = () => createIcons({ icons: ICONS });

// ---- header scrolled state (mirrors vectorspace.js) -----------------------
const header = document.querySelector('.vs-header');
if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
}

const WEEKDAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

const iso = (y, m0, d) =>
    `${y}-${String(m0 + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
        if (k === 'class') node.className = v;
        else if (k === 'html') node.innerHTML = v;
        else if (v !== null && v !== undefined) node.setAttribute(k, v);
    }
    for (const child of [].concat(children)) {
        if (child) node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    }
    return node;
};

// ---- schedule calendar ----------------------------------------------------
function initCalendar() {
    const dataEl = document.getElementById('vs-chess-calendar-data');
    const root = document.getElementById('vs-chess-cal');
    if (!dataEl || !root) return;

    const data = JSON.parse(dataEl.textContent);
    const months = data.months || [];
    if (!months.length) return;
    const openSet = new Set(data.open || []);
    const events = data.events || {};

    const titleEl = document.getElementById('vs-chess-cal-title');
    const tabsEl = document.getElementById('vs-chess-cal-tabs');
    const gridEl = document.getElementById('vs-chess-cal-grid');
    const detailEl = document.getElementById('vs-chess-detail');

    let activeMonth = 0;
    let selected = data.defaultSelected || null;

    function meta(isoDate) {
        // Returns the chess event payload for a date, or the generic open-day card.
        const ev = events[isoDate];
        if (ev) return ev;
        return {
            type: 'open', label: data.openLabel, color: data.openColor,
            lucide: data.openLucide, title: data.openTitle, note: data.openNote,
        };
    }

    function renderDetail() {
        if (!detailEl) return;
        detailEl.innerHTML = '';
        if (!selected) return;
        const [y, m, d] = selected.split('-').map(Number);
        const wd = WEEKDAYS_FULL[new Date(y, m - 1, d).getDay()];
        const info = meta(selected);

        detailEl.appendChild(el('div', { class: 'vs-chess-detail__weekday' }, wd));
        detailEl.appendChild(el('div', { class: 'vs-chess-detail__date' },
            `${MONTHS_FULL[m - 1]} ${d}`));
        const chip = el('div', { class: 'vs-chess-detail__chip' }, [
            el('span', { class: 'vs-chess-detail__chip-icon', style: `color:${info.color}` },
                el('i', { 'data-lucide': info.lucide })),
            el('span', {}, info.label),
        ]);
        detailEl.appendChild(chip);
        detailEl.appendChild(el('div', { class: 'vs-chess-detail__title' }, info.title));
        // info.note is the event's rich HTML (Event.description) — render it, don't escape.
        detailEl.appendChild(el('div', { class: 'vs-chess-detail__note', html: info.note }));
        detailEl.appendChild(el('div', { class: 'vs-chess-detail__hours' }, [
            el('i', { 'data-lucide': 'clock' }),
            // Special events carry their own hours; open club days fall back to
            // the static club hours label.
            el('span', {}, info.hours || data.hoursLabel),
        ]));
        // Nothing to act on once a day has passed: an add-to-calendar for last
        // month's tournament is noise, and "reserve a seat" is worse.
        const isPast = data.today && selected < data.today;
        if (isPast) {
            detailEl.appendChild(el('div', { class: 'vs-chess-detail__passed' },
                'This one has already happened.'));
            renderIcons();
            return;
        }
        // What the panel offers depends on the day. An event links to its own
        // page (or, failing that, offers an add-to-calendar); a plain drop-in
        // club day is only worth a CTA while passes are actually on sale.
        if (info.link) {
            detailEl.appendChild(el('a', {
                class: 'vs-btn vs-btn--accent vs-btn--full',
                href: info.link, target: '_blank', rel: 'noopener',
            }, [el('i', { 'data-lucide': 'external-link' }), el('span', {}, 'Event details')]));
        } else if (info.calLink) {
            detailEl.appendChild(el('a', {
                class: 'vs-btn vs-btn--accent vs-btn--full',
                href: info.calLink, target: '_blank', rel: 'noopener',
            }, [el('i', { 'data-lucide': 'calendar-plus' }), el('span', {}, 'Add to calendar')]));
        } else if (data.salesOpen) {
            detailEl.appendChild(el('a', {
                class: 'vs-btn vs-btn--accent vs-btn--full', href: '#pricing',
            }, 'Reserve a seat'));
        }
        renderIcons();
    }

    function select(isoDate) {
        selected = isoDate;
        renderGrid();
        renderDetail();
    }

    function renderGrid() {
        const { year, month } = months[activeMonth];
        const m0 = month - 1;
        if (titleEl) titleEl.textContent = months[activeMonth].label;

        gridEl.innerHTML = '';
        const startDow = (new Date(year, m0, 1).getDay() + 6) % 7; // Mon = 0
        const days = new Date(year, m0 + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < startDow; i++) cells.push(null);
        for (let d = 1; d <= days; d++) cells.push(d);
        while (cells.length % 7 !== 0) cells.push(null);

        for (let i = 0; i < cells.length; i += 7) {
            const week = el('div', { class: 'vs-chess-cal-week' });
            for (const d of cells.slice(i, i + 7)) {
                if (d === null) {
                    week.appendChild(el('div', { class: 'vs-chess-cal-cell vs-chess-cal-cell--blank' }));
                    continue;
                }
                const isoDate = iso(year, m0, d);
                const open = openSet.has(isoDate);
                const ev = events[isoDate];
                if (!open && !ev) {
                    // Not a club day and nothing special on it → inert.
                    week.appendChild(el('div', { class: 'vs-chess-cal-cell vs-chess-cal-cell--closed' }, String(d)));
                    continue;
                }
                // Club days get the open style; a special event on a non-club day
                // (e.g. a pre-season social) is still clickable + dotted.
                const classes = ['vs-chess-cal-cell', open ? 'vs-chess-cal-cell--open' : 'vs-chess-cal-cell--event'];
                if (isoDate === selected) classes.push('is-selected');
                if (ev) classes.push('has-event');
                // Days that have already happened stay browsable — the calendar
                // is a record too — but read as past rather than as something
                // you can still turn up to.
                if (data.today && isoDate < data.today) classes.push('is-past');
                const cell = el('button', { class: classes.join(' '), type: 'button' }, String(d));
                if (ev) {
                    cell.appendChild(el('span', {
                        class: 'vs-chess-cal-dot',
                        style: `background:${ev.color}`,
                    }));
                }
                cell.addEventListener('click', () => select(isoDate));
                week.appendChild(cell);
            }
            gridEl.appendChild(week);
        }
    }

    // The tab strip has to survive two things that move independently: the
    // number of months (which grows as events get scheduled further out) and
    // the width it has to sit in. Rather than pick a breakpoint for either,
    // try the roomiest layout and step down only when it genuinely doesn't
    // fit — full names, then three-letter names, then tabs on their own row
    // under the title. Left to itself the strip just scrolls, which reads as
    // clipped on a desktop where there's obviously room.
    function fitTabs() {
        if (!tabsEl) return;
        const head = tabsEl.closest('.vs-chess-cal-head');
        const overflows = () => tabsEl.scrollWidth > tabsEl.clientWidth + 1;
        tabsEl.classList.remove('is-compact');
        head.classList.remove('is-stacked');
        if (!overflows()) return;
        tabsEl.classList.add('is-compact');
        if (!overflows()) return;
        head.classList.add('is-stacked');
    }

    // month tabs
    if (tabsEl) {
        months.forEach((mo, idx) => {
            // Both spellings ride along and CSS picks one: six full month names
            // don't fit a phone, six three-letter ones do, and deciding that in
            // JS would mean re-rendering on resize.
            const name = mo.label.split(' ')[0]; // "July", "August"
            const btn = el('button', {
                class: 'vs-chess-cal-tab' + (idx === activeMonth ? ' is-active' : ''),
                type: 'button',
            }, [
                el('span', { class: 'vs-chess-cal-tab__full' }, name),
                el('span', { class: 'vs-chess-cal-tab__abbr' }, name.slice(0, 3)),
            ]);
            btn.addEventListener('click', () => {
                activeMonth = idx;
                tabsEl.querySelectorAll('.vs-chess-cal-tab').forEach((b, i) =>
                    b.classList.toggle('is-active', i === idx));
                renderGrid();
            });
            tabsEl.appendChild(btn);
        });
    }

    // Open on the month that holds the default selection.
    if (selected) {
        const selMonth = months.findIndex(mo => selected.startsWith(`${mo.year}-${String(mo.month).padStart(2, '0')}`));
        if (selMonth >= 0) {
            activeMonth = selMonth;
            if (tabsEl) tabsEl.querySelectorAll('.vs-chess-cal-tab').forEach((b, i) =>
                b.classList.toggle('is-active', i === activeMonth));
        }
    }
    renderGrid();
    renderDetail();
    fitTabs();
    // Re-fit when the card resizes: a rotated phone or a dragged window changes
    // what fits, and the layout should step back up as well as down.
    if (typeof ResizeObserver === 'function') {
        new ResizeObserver(fitTabs).observe(root);
    }

    // The hero's "next up" strip and each poster card's "find it on the
    // calendar" button select that date below and scroll the calendar into view.
    document.querySelectorAll('[data-chess-jump]').forEach(trigger => {
        trigger.addEventListener('click', () => {
            const target = trigger.getAttribute('data-chess-jump');
            const monthIdx = months.findIndex(mo =>
                target.startsWith(`${mo.year}-${String(mo.month).padStart(2, '0')}`));
            if (monthIdx >= 0) {
                activeMonth = monthIdx;
                if (tabsEl) tabsEl.querySelectorAll('.vs-chess-cal-tab').forEach((b, i) =>
                    b.classList.toggle('is-active', i === monthIdx));
            }
            select(target);
            document.getElementById('schedule')?.scrollIntoView({ behavior: 'smooth' });
        });
    });
}

// ---- family estimator + pass selection ------------------------------------
function money(cents) {
    return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

// 13% Ontario HST, added on top of every posted price at checkout. Display
// only — the server recomputes authoritatively (website/tax.py, same
// half-up rounding for positive amounts).
const HST_RATE = 0.13;
function hstCents(subtotalCents) {
    return Math.round(subtotalCents * HST_RATE);
}

function initRegistration() {
    const form = document.getElementById('vs-chess-form');
    const estimator = document.getElementById('vs-chess-estimator');
    const famControl = document.getElementById('vs-chess-famsize');

    // Family per-person rates + initial counts live as data attributes on the
    // pricing card's estimator (present whenever the family option is offered).
    let adultCents = Number(estimator?.getAttribute('data-adult-cents')) || 0;
    let childCents = Number(estimator?.getAttribute('data-child-cents')) || 0;
    let adults = Number(estimator?.getAttribute('data-adults')) || 1;
    let kids = Number(estimator?.getAttribute('data-kids')) || 0;

    const passInput = form?.querySelector('[name="pass_type"]');
    const adultInput = form?.querySelector('[name="adult_count"]');
    const childInput = form?.querySelector('[name="child_count"]');

    const isFamily = () => passInput && passInput.value === 'family';
    const familyTotalCents = () => adults * adultCents + kids * childCents;

    // The pre-tax price of whatever the form currently has selected: the
    // family estimate, or the fixed pass's data-cents off its <option>.
    function selectedSubtotalCents() {
        if (isFamily()) return familyTotalCents();
        const opt = passInput?.selectedOptions?.[0];
        return Number(opt?.getAttribute('data-cents')) || 0;
    }

    // "Due at checkout" line under the form: subtotal + HST = total. The
    // posted prices everywhere else stay ex-tax, so this is the one place
    // the customer sees the real charge before Clover does.
    const dueLine = document.getElementById('vs-chess-due');
    function refreshDue() {
        if (!dueLine) return;
        const subtotal = selectedSubtotalCents();
        if (subtotal <= 0) { dueLine.textContent = '13% HST is added at checkout.'; return; }
        const tax = hstCents(subtotal);
        dueLine.textContent =
            `Due at checkout: ${money(subtotal + tax)} (${money(subtotal)} + ${money(tax)} HST)`;
    }

    // Push the shared adults/kids state to every display (the pricing-card
    // estimator AND the in-form control's totals) and the hidden fields. Fixed
    // passes show their price in the <select> option itself, so there's no
    // separate summary to keep in sync.
    function refreshFamily() {
        document.querySelectorAll('[data-fam="adults"]').forEach(el => { el.textContent = String(adults); });
        document.querySelectorAll('[data-fam="kids"]').forEach(el => { el.textContent = String(kids); });
        document.querySelectorAll('[data-fam="total"]').forEach(el => { el.textContent = money(familyTotalCents()); });
        if (adultInput) adultInput.value = String(adults);
        if (childInput) childInput.value = String(kids);
        refreshDue();
    }

    // Reveal the in-form family-size control only when the family pass is chosen.
    function syncFamilyControl() {
        if (famControl) famControl.classList.toggle('is-shown', isFamily());
    }

    function selectPass(passType) {
        if (passInput) passInput.value = passType;
        syncFamilyControl();
        refreshFamily();
        document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' });
    }

    document.querySelectorAll('[data-pass-type]').forEach(btn => {
        btn.addEventListener('click', () => selectPass(btn.getAttribute('data-pass-type')));
    });

    // Manual dropdown change toggles the in-form control + refreshes the total.
    passInput?.addEventListener('change', () => {
        syncFamilyControl();
        refreshFamily();
    });

    // Every stepper — the pricing-card estimator and the in-form control — drives
    // the one shared adults/kids state, so the two never drift apart.
    document.querySelectorAll('[data-step]').forEach(btn => {
        btn.addEventListener('click', () => {
            const which = btn.getAttribute('data-step'); // adult+ / adult- / kid+ / kid-
            if (which === 'adult+') adults = Math.min(8, adults + 1);
            if (which === 'adult-') adults = Math.max(1, adults - 1);
            if (which === 'kid+') kids = Math.min(10, kids + 1);
            if (which === 'kid-') kids = Math.max(0, kids - 1);
            refreshFamily();
        });
    });

    syncFamilyControl();
    refreshFamily();
}

// ---- visit: toggle the live map on/off over the storefront photo ----------
function initMapToggle() {
    const panel = document.getElementById('vs-chess-map');
    const btn = panel?.querySelector('[data-map-toggle]');
    if (!panel || !btn) return;
    const frame = panel.querySelector('.vs-chess-map-frame');
    btn.addEventListener('click', () => {
        const on = panel.classList.toggle('is-map-on');
        btn.setAttribute('aria-pressed', String(on));
        // Lazy-load the map only the first time it's shown.
        if (on && frame && !frame.src) frame.src = frame.dataset.src;
    });
}

renderIcons();
initChessHero();
initCalendar();
initRegistration();
initMapToggle();

// After a failed submission the page reloads at #register with an error banner;
// the native anchor handles scrolling. Nothing else to do here.
